import { EventEmitter } from '@angular/core';

import * as SVG from 'svg.js';
import { applyContentImageSwapping, DragDropGhostSVG } from '../drag-drop';

import { ImageQualityRatingService
       } from 'app/controls/image-quality-rating/image-quality-rating.service';
import { QualityRating
       } from 'app/controls/image-quality-rating/quality-rating';
import { injectMultiversionImageBehavior, loadGalleryImage
       } from 'app/controls/multiversion-image/multiversion-image';
import { GalleryImage, GalleryService } from 'app/gallery/gallery.service';
import { ToolboxService } from 'app/toolboxes/toolbox.service';
import { importImageStoreImage
       } from '../../../gallery/imageprovider';
import { ImageProvisioningService
       } from '../../../gallery/imageprovider.service';
import { DesignStepService } from '../../design-step/design-step.service';

import { CoordinateTranslation
       } from 'app/controls/image-cropper/coordinate-translation';
import { Region } from 'app/controls/image-cropper/region';
import { GalleryConstants, GlobalConstants, StageConstants
       } from 'core/constants';
import { TrivialError } from 'core/error/error';
import { ErrorService } from 'core/error/error.service';
import { Globals } from 'core/globals';
import { ImageStoreService } from 'core/imagestore/imagestore.service';
import { LoggingService } from 'core/logging/logging.service';
import { Utils } from 'core/utils';
import { StageUtils } from 'core/utils.stage';
import { CopyPlaceConfig } from '../../collage-designer-config';
import { CopyRules } from '../../copy-rules';
import { ActionBarDirective } from './overlays/action-bar/action-bar.directive';
import { QualityRatingIconComponent
       } from './overlays/quality-rating-icon/quality-rating-icon.component';
import { QualityRatingIconDirective
       } from './overlays/quality-rating-icon/quality-rating-icon.directive';
import { Placeholder, PlaceholderType } from './placeholder';
import { PlaceholderContentService } from './placeholder-content.service';
import { PlaceholderService } from './placeholder.service';

export interface BoundingBox
{
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ImageInformation
{
  id: string;
  galleryImage: GalleryImage;
  isUnchanged: boolean;
}

export class ImageLoadingError extends TrivialError
{
  // i18n.error.imageLoadingError.title
  // i18n.error.imageLoadingError.message
  protected _name: string = 'ImageLoadingError';
}

export class ImagePlaceholder extends Placeholder
{
  public placeholderHighlighter: SVG.Image | undefined = undefined;
  public defaultBoundingBox: BoundingBox | undefined = undefined;
  public removeAllPlaceholderHighlighters: EventEmitter<boolean> =
      new EventEmitter();

  private qualityIndicatorId: string | undefined;
  private qualityRatingIcon: QualityRatingIconComponent | undefined = undefined;
  private qualityRatingIconContainer:
      QualityRatingIconDirective | undefined = undefined;
  private contentImageSwapping: DragDropGhostSVG;
  private readonly QUALITY_RATING_IMAGE_PATH: string =
      '/assets/img/image-quality-rating/quality-{rating}.svg';
  private readonly PLACEHOLDER_HIGHLIGHTER_IMAGE_PATH: string =
      '/assets/img/highlighter.svg';
  private readonly eventListeners: Object = {
    'dragStart': this.dragStart.bind(this),
    'dragenter': this.dragEnter.bind(this),
    'dragover': Utils.eventPreventDefault,
    'dragleave': this.dragLeave.bind(this),
    'dragEnd': this.dragEnd.bind(this),
    'drop': this.dropImage.bind(this),
    'dropSwap': this.swapImage.bind(this),
    'click': this.clickEvent.bind(this),
    'mouseover': this.mouseOverImage.bind(this),
    'mouseleave': this.mouseLeave.bind(this)
  };

  constructor(
      errorService: ErrorService,
      loggingService: LoggingService,
      designStepService: DesignStepService,
      galleryService: GalleryService,
      private imageQualityRatingService: ImageQualityRatingService,
      private imageProvisioningService: ImageProvisioningService,
      private imageStoreService: ImageStoreService,
      toolboxService: ToolboxService,
      placeholderContentService: PlaceholderContentService,
      placeholderService: PlaceholderService,
      actionBarContainer: ActionBarDirective,
      qualityRatingIconContainer: QualityRatingIconDirective,
      image: SVG.Image,
      partId: string,
      isLocked: boolean)
  {
    super(
        errorService, loggingService, designStepService, galleryService,
        toolboxService, placeholderContentService, placeholderService,
        actionBarContainer, image, partId, PlaceholderType.Image, isLocked);

    this.qualityIndicatorId = this.wrapper.id() + '-quality-indication';

    this.imageQualityRatingService = imageQualityRatingService;
    this.imageProvisioningService = imageProvisioningService;
    this.imageStoreService = imageStoreService;
    this.designStepService = designStepService;
    this.qualityRatingIconContainer = qualityRatingIconContainer;
  }

  public async setup(): Promise<void>
  {
    injectMultiversionImageBehavior(this.element);

    if (this.zoomMode === StageConstants.ZOOM_MODE_CUT) {
      this.wrapper.attr('preserveAspectRatio', 'xMidYMid slice');
    }

    this.applyPendingListener();
    this.applyImageLoadedBehaviour();

    if (this.isContentChangeable()) {
      this.applyChangeableEventListeners();
      this.contentImageSwapping = applyContentImageSwapping(this);

      if (!this.isLinked() && this.isUnchanged()) {
        await this.reset();
      }
      if (this.get(StageConstants.ATTRIBUTE_BOUNDING_BOX)) {
        this.defaultBoundingBox = this.getBoundingBox();
      }
    }

    if (this.isLinked()) {
      await this.createLinkedImage();
    }

    this.updateQualityRating();
  }

  public async destructor(): Promise<void>
  {
    await super.destructor();
    this.hidePlaceholderHighlighter();
    if (this.isContentChangeable()) {
      this.removeChangeableEventListeners();
      this.contentImageSwapping.destructor();
    }
    if (this.qualityRatingIcon) {
      this.qualityRatingIcon.destroy();
    }
  }

  public highlight(): void
  {
    this.showHighlightMarker();
  }

  public isFlexible(): boolean
  {
    if (Globals.mode === GlobalConstants.MODE_GOD) {
      return true;
    }

    return false;
  }

  public hasEditableContent(): boolean
  {
    return this.getImageModel() !== undefined;
  }

  public async extractInformation(): Promise<ImageInformation | undefined>
  {
    const galleryImage = this.getImageModel();
    return galleryImage
      ? { id: this.get('id'),
          galleryImage: galleryImage,
          isUnchanged: this.isUnchanged(),
        }
      : undefined;
  }

  public async applyInformation(info: ImageInformation): Promise<void>
  {
    if (this.galleryService.isImageUsed(info.galleryImage.id)) {
      return;
    }

    await this.loadGalleryImage(info.galleryImage);
  }

  private applyPendingListener(): void
  {
    const observer = new MutationObserver(mutations => {
      if (this.isPending()) {
        this.showLoadingMarker();
        const subscription = this.galleryService.uploadFailed
          .subscribe((wrapper) => {
            this.hideLoadingMarker(wrapper);
            subscription.unsubscribe();
          });
      } else {
        this.hideLoadingMarker();
      }
    });

    observer.observe(this.element.node, {
        attributes: true,
        attributeFilter: [ StageConstants.ATTRIBUTE_PENDING ]
    });
  }

  private applyImageLoadedBehaviour(): void
  {
    this.element.error(this.error.bind(this));

    this.element.loaded(this.loaded.bind(this));
  }

  private applyChangeableEventListeners(): void
  {
    const image = this.element.node;

    const observer = new MutationObserver(mutations => {
      this.changed.emit(this.element.id());
    });
    observer.observe(image, {attributes: true});

    for (const listener in this.eventListeners) {
      if (this.eventListeners.hasOwnProperty(listener)) {
        image.addEventListener(listener, this.eventListeners[listener]);
      }
    }
  }

  private removeChangeableEventListeners(): void
  {
    const image = this.element.node;

    for (const listener in this.eventListeners) {
      if (this.eventListeners.hasOwnProperty(listener)) {
        image.removeEventListener(listener, this.eventListeners[listener]);
      }
    }
  }

  private isDroppable(): boolean
  {
    return !this.isPending() &&
        this.galleryService.getDraggingImage() &&
        (this.getImageModel() === undefined ||
         this.getImageModel().id !==
         this.galleryService.getDraggingImage().id);
  }

  private dragStart(event: Event): void
  {
    this.galleryService.setDraggingImage(this.getImageModel().id);
  }

  private dragEnter(event: Event): void
  {
    if (this.galleryService.getDraggingImage() && this.isDroppable()) {
      this.showDropMarker();
    }
  }

  private dragLeave(event: Event): void
  {
    this.hideDropMarker();
  }

  private dragEnd(event: Event): void
  {
    this.galleryService.setDraggingImage(undefined);
  }

  private async dropImage(event: Event): Promise<void>
  {
    event.preventDefault();
    this.hideDropMarker();

    let draggingImage = this.galleryService.getDraggingImage();
    if (draggingImage !== undefined && this.isDroppable()) {
      await this.loadGalleryImage(draggingImage);
      this.galleryService.setDraggingImage(undefined);
    }
  }

  private async swapImage(event): Promise<void>
  {
    this.galleryService.setSelectedImage(undefined);
    this.removeAllPlaceholderHighlighters.emit(true);
    this.hideDropMarker();

    if (!this.isDroppable() || event.sourcePlaceholder.isPending()) {
      return;
    }

    const galleryImage = this.getImageModel();
    const sourcePlaceholderLoad = galleryImage ?
      event.sourcePlaceholder.loadGalleryImage(galleryImage) :
      event.sourcePlaceholder.reset();

    await Promise.all([
        this.loadGalleryImage(this.galleryService.getDraggingImage()),
        sourcePlaceholderLoad]);

    this.finished.emit(this.galleryService.getDraggingImage());
    this.galleryService.setDraggingImage(undefined);
  }

  private async mouseOverImage(event: Event): Promise<void>
  {
    await super.mouseOver();
    if (!this.placeholderService.hasTransformHandle()) {
      this.wrapper.node.style.cursor = 'pointer';
    }
  }

  private showDropMarker(): void
  {
    this.wrapper
      .rect()
      .size('100%', '100%')
      .move(this.wrapper.viewbox().x, this.wrapper.viewbox().y)
      .addClass('drop-target-marker');
  }

  private hideDropMarker(): void
  {
    const marker = this.wrapper.select('.drop-target-marker').first();
    if (marker) {
      marker.remove();
    }
  }

  private showLoadingMarker(): void
  {
    if (this.wrapper
        .select(`.${StageConstants.CLASS_NAME_LOADING_MARKER}`)
        .first()) {
      return;
    }
    const box = this.wrapper.viewbox();

    const markerWidth = box.width / 4;
    const markerHeight = box.height / 4;

    this.wrapper
      .image()
      .load(GalleryConstants.LOADING_ICON_URL)
      .size(markerWidth, markerHeight)
      .move(box.x + (box.width - markerWidth) / 2,
            box.y + (box.height - markerHeight) / 2)
      .addClass(StageConstants.CLASS_NAME_LOADING_MARKER);

    this.element.addClass(
        StageConstants.CLASS_NAME_HIDE_PLACEHOLDER_THROUGH_OPACITY);
  }

  private hideLoadingMarker(target?: SVG.Doc): void
  {
    const wrapper = target ? target : this.wrapper;
    const marker = wrapper
      .select(`.${StageConstants.CLASS_NAME_LOADING_MARKER}`)
      .first();
    if (marker) {
      marker.remove();
    }

    this.element.removeClass(
        StageConstants.CLASS_NAME_HIDE_PLACEHOLDER_THROUGH_OPACITY);
  }

  private async createLinkedImage(): Promise<void>
  {
    this.detachContent();

    const currentPartId = this.designStepService.getPartId();
    const copyFrom = this.get(StageConstants.ATTRIBUTE_COPY_FROM);

    if (! copyFrom) {
      return;
    }

    let link = undefined;

    try {
      link = CopyRules.decodePlace(copyFrom, currentPartId);
    }
    catch (e) {
      this.errorService.add(e, 'PlaceholderImage');
      this.errorService.flush('PlaceholderImage');
    }

    if (link.item === undefined) {
      this.fillWithStageSnapshot(link.part);
    }
    else {
      if (link.part === currentPartId) {
        const superiorImage = SVG.get(link.item) as SVG.Image;
        if (! superiorImage) {
          return;
        }
        this.createMutationObserverForLinkedElement(superiorImage)
            .observe(superiorImage.node, {
                attributes: true,
                attributeOldValue: true
            });
      }
      else {
        await this.adoptItemFromAnotherPart(link);
     }
   }
 }

  private async adoptItemFromAnotherPart(link: CopyPlaceConfig): Promise<void>
  {
    const structure =
        await this.designStepService.getSVGStructureByPartId(link.part);
    if (!structure) {
      return;
    }
    const doc = (new DOMParser()).parseFromString(structure, 'image/svg+xml');

    const superiorImage = SVG.adopt(doc.getElementById(link.item)) as SVG.Image;
    if (!superiorImage) {
      return;
    }
    const superiorImageHref = superiorImage.attr(StageConstants.ATTRIBUTE_HREF);
    const superiorImageBBox = superiorImage.attr(
       StageConstants.ATTRIBUTE_BOUNDING_BOX);

    this.element.loaded(this.loaded.bind(this));
    this.element.load(superiorImageHref);

    const galleryImage = this.parseImageModel(superiorImage.attr(
        StageConstants.ATTRIBUTE_IMAGE_MODEL));
    if (galleryImage) {
      this.setImageModel(galleryImage);
      this.galleryService.setImageAsUsed(galleryImage.id);
    }

    this.set(StageConstants.ATTRIBUTE_SOURCE_IMG_DIMENSIONS,
       superiorImage.attr(StageConstants.ATTRIBUTE_SOURCE_IMG_DIMENSIONS));
    const boundingBox = JSON.parse(superiorImageBBox);
    this.applyBoundingBox(boundingBox);

    this.removePlaceholderImageIfNecessary();
  }

  private renameConflictingElements(
    hostDoc: Element, embedded: Element): Element
  {
    const hostIds = StageUtils.listElementIds(hostDoc);
    const embeddedReferences = StageUtils.findReferencedElements(embedded);
    const conflicting =
      embeddedReferences.filter(it => hostIds.indexOf(it.referencedId) !== -1);

    for (const it of conflicting) {
      const conflictFreeId = `${it.referencedId}-${Utils.getRandomId()}`;
      it.reference.setAttribute('id', conflictFreeId);
      it.element.setAttribute(it.attribute, `url(#${conflictFreeId})`);
    }

    return embedded;
  }

  private async fillWithStageSnapshot(partId: string):
      Promise<void>
  {
    // get selected template structure
    const structure =
        await this.designStepService.getSVGStructureByPartId(partId);
    if (!structure) {
      return;
    }

    // everything will be inserted into the current stage
    const targetSvg = this.element.doc();

    // the structure will be transformed inside a temporary area,
    // because SVG.js does not work that well with detached documents.
    const doc = (new DOMParser()).parseFromString(
        StageUtils.sanitizeStructure(structure),
        'image/svg+xml'
    );

    const tempArea = targetSvg.nested();
    tempArea.node.appendChild(
      this.renameConflictingElements(
        targetSvg.node as Element,
        doc.firstChild as Element));

    const svg = SVG(tempArea.node.firstChild);

    for (let prop in svg.attr()) {
      if (prop.startsWith('xmlns') && !targetSvg.hasOwnProperty(prop)) {
        targetSvg.attr(prop, svg.attr(prop));
      }
    }

    // Collect defs and add to own defs
    const targetDefs = targetSvg.defs();

    svg.select('defs').each(function(): void {
      for (const def of this.children()) {
        if (targetDefs.select('#' + def.id()).members.length === 0) {
          def.addTo(targetDefs);
        }
      }
    });

    // strip defs, metadata from document
    const stripElementByTag = (el: SVG.Doc, tag: string) => {
      el.select(tag).each(function (): void { this.remove(); });
    };

    stripElementByTag(svg, 'namedview');
    stripElementByTag(svg, 'metadata');
    stripElementByTag(svg, 'defs');

    // remove pap-attributes except pap:href
    const stripPapAttributes = (el: SVG.Element) => {
      const whitelist = [StageConstants.ATTRIBUTE_HREF];

      for (const attrName of Object.keys(el.attr())) {
        if (attrName.startsWith('pap:') && whitelist.indexOf(attrName) === -1) {
          el.node.removeAttribute(attrName);
        }
      }
    };

    svg.select('*').each(function(): void {
      stripPapAttributes(this);
    });

    // apply viewbox
    this.wrapper.viewbox(svg.viewbox());

    // clone elements into wrapper
    for (const c of svg.children()) {
      (<any>c).clone(this.wrapper);
    }

    // remove original image
    this.element.remove();

    // mark wrapper as copy
    this.wrapper.attr(StageConstants.ATTRIBUTE_COPIED_FROM, `//${partId}`);
    this.wrapper.attr('preserveAspectRatio', 'none');

    const image =
        SVG.adopt(<any>this.wrapper.node.querySelector('image')) as SVG.Image;
    this.removePlaceholderImageIfNecessary(image);

    // remove temporary
    tempArea.remove();
  }

  private createMutationObserverForLinkedElement(superiorImage: SVG.Image):
      MutationObserver
  {
    const indexOfWithoutNamespace = (a, b) =>
        a.map((e) => { return e.attributeName.replace(/pap:/, ''); })
         .indexOf(b.replace(/pap:/, '')) !== -1;

    return new MutationObserver(mutations => {
      const superiorImageHref =
          superiorImage.attr(StageConstants.ATTRIBUTE_HREF);
      const imageHref = this.getHref();
      const superiorImageBBox =
          superiorImage.attr(StageConstants.ATTRIBUTE_BOUNDING_BOX);
      const imageBBox = this.get(StageConstants.ATTRIBUTE_BOUNDING_BOX);

      const imageChanged =
          indexOfWithoutNamespace(mutations, StageConstants.ATTRIBUTE_HREF) &&
          superiorImageHref !==
              superiorImage.attr(StageConstants.ATTRIBUTE_PLACEHOLDER_URL) &&
          superiorImageHref !== imageHref;

      const boundingBoxChanged = indexOfWithoutNamespace(mutations,
          StageConstants.ATTRIBUTE_BOUNDING_BOX) &&
          superiorImageHref !==
              superiorImage.attr(StageConstants.ATTRIBUTE_PLACEHOLDER_URL) &&
          superiorImageBBox !== imageBBox;

      const placeholderImageSet =
          indexOfWithoutNamespace(mutations, StageConstants.ATTRIBUTE_HREF) &&
          superiorImageHref ===
              superiorImage.attr(StageConstants.ATTRIBUTE_PLACEHOLDER_URL) &&
          imageHref !== superiorImageHref;

      if (placeholderImageSet) {
        this.element.loaded(this.loaded.bind(this));
        this.element.load(superiorImageHref);
      }

      if (imageChanged) {
        const galleryImage = this.parseImageModel(
          superiorImage.attr(StageConstants.ATTRIBUTE_IMAGE_MODEL));

        this.element.loaded(_ => this.loaded(galleryImage, true));
        loadGalleryImage(this.element, galleryImage);
      }

      if (boundingBoxChanged) {
        this.set(StageConstants.ATTRIBUTE_SOURCE_IMG_DIMENSIONS,
            superiorImage.attr(StageConstants.ATTRIBUTE_SOURCE_IMG_DIMENSIONS));
        const boundingBox = JSON.parse(superiorImageBBox);
        this.applyBoundingBox(boundingBox);
      }

      this.removePlaceholderImageIfNecessary();
    });
  }

  private removePlaceholderImageIfNecessary(_image?: SVG.Image): void
  {
    const image = _image ? _image : this.element;
    const isNecessary = !this.isContentChangeable() &&
        image.attr(StageConstants.ATTRIBUTE_HREF) &&
        image.attr(StageConstants.ATTRIBUTE_HREF) ===
            this.get(StageConstants.ATTRIBUTE_PLACEHOLDER_URL);

    if (isNecessary) {
      image.attr(StageConstants.ATTRIBUTE_HREF, '');
      image.node.setAttributeNS('http://www.w3.org/1999/xlink', 'href', '');
    }
  }

  private useDefaultCrop(): boolean
  {
    // defaultCrop nur dann benutzen, wenn es sich bei dem aktuellen Template
    // um das Initiale oder ein davon abgeleitetes (fertig gestaltetes Produkt)
    // handelt.
    if (this.designStepService.getSVGStructureUrl() ===
        this.designStepService.getInitialSVGStructureUrl() ||
        this.element.doc().attr(StageConstants.ATTRIBUTE_INITIAL_STRUCTURE_URL)
            === this.designStepService.getInitialSVGStructureUrl()) {
      return true;
    }
    return false;
  }

  private applyZoomMode(imageInfo: GalleryImage|any): void
  {
    let boundingBox: BoundingBox;
    const dimensions = { width: imageInfo.width, height: imageInfo.height, };
    this.set(StageConstants.ATTRIBUTE_SOURCE_IMG_DIMENSIONS,
        JSON.stringify(dimensions));

    if (this.useDefaultCrop()) {
      boundingBox = imageInfo.defaultCrop;
    }

    if (!boundingBox) {
      const wrapperRegion = Region.fromSimilar({
          x: 0,
          y: 0,
          width: this.wrapper.width(),
          height: this.wrapper.height(),
      });
      const imageRegion = Region.fromSimilar({
          x: 0,
          y: 0,
          width: imageInfo.width,
          height: imageInfo.height,
      });

      const translation = new CoordinateTranslation(
          wrapperRegion, imageRegion, imageInfo.contentCenter);
      boundingBox = this.zoomMode === StageConstants.ZOOM_MODE_CUT
          ? translation.regionToViewportSpace(wrapperRegion)
          : imageRegion;
    }

    this.applyBoundingBox(boundingBox);
    this.defaultBoundingBox = boundingBox;
  }

  public applyBoundingBox(crop: BoundingBox): void
  {
    this.wrapper.viewbox(crop);

    this.set(StageConstants.ATTRIBUTE_BOUNDING_BOX, JSON.stringify(crop));
    (<any>this.element).scheduleReload();
  }

  private loaded(imgInfo?: GalleryImage|any,
                 initial?: boolean): void
  {
    if (initial && imgInfo) {
      this.applyZoomMode(imgInfo);
      this.element.width(imgInfo.width);
      this.element.height(imgInfo.height);
    }

    this.updateQualityRating();
    this.changed.emit(this.element.id());
    this.element.loaded(() => {});

    if (this.hasFocus()) {
      if (StageConstants.BLUR_PLACEHOLDER_AFTER_ACTION) {
        this.blur();
      } else {
        if (initial) {
          this.focus(); // to emit FocusFilled after FocusEmpty
        }
      }
    }
  }

  private async error(error: Error): Promise<void>
  {
    if (!this.isUnchanged()) {
      const imageUrl = this.element.node.getAttributeNS(
          'http://www.w3.org/1999/xlink', 'href');
      let trivialError = new ImageLoadingError(
          'image could not be loaded', [ imageUrl ]);

      this.errorService.add(trivialError, 'PlaceholderImage');
      this.errorService.flush('PlaceholderImage');
      this.galleryService
        .removeImage(this.getImageModel().id);
      await this.reset();
    }
    else {
      // If placeholder image not reachable
      this.element.load(StageConstants.PIXEL_WHITE);
    }
  }

  public async reset(imageUrl?: string): Promise<void>
  {
    await super.reset();
    this.set(StageConstants.ATTRIBUTE_QUALITY_RATING, undefined);

    const galleryImage = this.getImageModel();
    if (galleryImage) {
      this.galleryService.setImageAsUnused(galleryImage.id);
      this.set(StageConstants.ATTRIBUTE_IMAGE_MODEL, undefined);
    }

    const cleared = imageUrl === undefined;
    return new Promise<void>((resolve) => {
      this.element.loaded((imgInfo) => {
        this.loaded(imgInfo, true);
        if (cleared) {
          this.finished.emit();
        }
        resolve();
      });

      imageUrl = imageUrl ? imageUrl :
          this.get(StageConstants.ATTRIBUTE_PLACEHOLDER_URL);
      this.element.load(imageUrl);
    });
  }

  public hideQualityIndication(): void
  {
    if (!StageConstants.SHOW_QUALITY_RATING_SMILEY) {
      return;
    }

    const qualityIndicator = SVG.get(this.qualityIndicatorId) as SVG.Image;
    if (qualityIndicator) {
      qualityIndicator.remove();
    }
  }

  public showQualityIndication(): void
  {
    if (!StageConstants.SHOW_QUALITY_RATING_SMILEY) {
      return;
    }

    if (!this.isUnchanged()) {
      let qualityIndicator = SVG.get(this.qualityIndicatorId) as SVG.Image;
      const qualityRating = this.getQualityRating();
      const icon = this.determineQualityIndicatorIcon(qualityRating);

      if (qualityIndicator) {
        qualityIndicator.load(icon);
      }
      else {
        qualityIndicator = this.wrapper
          .image(icon)
          .addClass(StageConstants.CLASS_NAME_QUALITY_INDICATION)
          .id(this.qualityIndicatorId);
      }

      qualityIndicator.loaded((imgInfo) => {
        const vbox = this.wrapper.viewbox();

        qualityIndicator
          .width(vbox.width / 2)
          .height(vbox.height / 2)
          .x(vbox.width / 4 + vbox.x)
          .y(vbox.height / 4 + vbox.y);
      });
    }
    else {
      this.hideQualityIndication();
    }
  }

  private determineQualityIndicatorIcon(qualityRating: number): string
  {
    return this.QUALITY_RATING_IMAGE_PATH
      .replace('{rating}', QualityRating.getQualityString(qualityRating));
  }

  public getQualityRating(): number
  {
    return parseInt(this.get(StageConstants.ATTRIBUTE_QUALITY_RATING), 10);
  }

  public updateQualityIndication(): void
  {
    if (!StageConstants.SHOW_QUALITY_RATING_ICON) {
      return;
    }

    if (!this.qualityRatingIcon) {
      this.qualityRatingIcon =
          this.qualityRatingIconContainer.createComponent();
      this.qualityRatingIcon.initialize(this);
    }
    this.qualityRatingIcon.updateQualityIndication();
  }

  public async restoreGalleryImageIfNecessary(): Promise<void>
  {
    let model = this.getImageModel();

    if (!model) {
      // TODO: Remove me at the earliest after 2026-06-01
      if (this.get('pap:gallery-image-id') !== undefined) {
        model = await this.tryGenerateModel(this.getHref());
      }
      if (!model) {
        return;
      }
    }

    if (!this.galleryService.getImageById(model.id)) {
      await this.galleryService.addImage(model);
    }
    this.galleryService.setImageAsUsed(model.id);
  }

  // TODO: Remove me at the earliest after 2026-06-01
  private async tryGenerateModel(url: string): Promise<GalleryImage|undefined>
  {
    try {
      const info = await this.imageStoreService.getImageInfo(url);
      const model =
          await importImageStoreImage(info)('im' + Utils.getRandomId(24));
      this.setImageModel(model);
      return model;
    }
    catch (error) {
      console.error(error);
      return undefined;
    }
  }

  public async loadLocalImage(input: HTMLInputElement): Promise<void>
  {
    await this.imageProvisioningService.addUserFileToPlaceholder(
        input, this.element);
    this.blur(true);
  }

  public async loadGalleryImage(galleryImageOrId: GalleryImage | string):
      Promise<void>
  {
    return new Promise<void>(async (resolve, reject) => {
      try {
        const galleryImage = typeof(galleryImageOrId) === 'string'
            ? this.galleryService.getImageById(galleryImageOrId)
            : galleryImageOrId;

        // We need to ensure the placeholder is pending, to avoid parallel
        // critical user actions.
        this.set(StageConstants.ATTRIBUTE_PENDING, galleryImage.id);

        const previousModel = this.getImageModel();
        if (previousModel) {
          this.galleryService.setImageAsUnused(previousModel.id);
        }

        const wasUnchanged = this.isUnchanged();
        await this.commitContent();

        const loadTransaction = this.startTransaction(() => {
          if (wasUnchanged) {
            this.detachContent();
          }

          this.set(StageConstants.ATTRIBUTE_PENDING, undefined);
        });

        this.setImageModel(galleryImage);
        this.galleryService.setImageAsUsed(galleryImage.id);

        this.element.loaded(async (imgInfo) => {
          try {
            this.loaded(galleryImage, true);
            loadTransaction.commit();
            await this.commitContent();
          }
          finally {
            this.set(StageConstants.ATTRIBUTE_PENDING, undefined);
          }
          resolve();
        });
        this.element.error(async (error) => {
          try {
            await this.error(error);
          }
          finally {
            this.set(StageConstants.ATTRIBUTE_PENDING, undefined);
          }
          resolve();
        });

        loadGalleryImage(this.element, galleryImage);
      }
      catch (error) {
        this.set(StageConstants.ATTRIBUTE_PENDING, undefined);
        throw error;
      }
    });
  }

  private setImageModel(galleryImage: GalleryImage): void
  {
    this.set(
        StageConstants.ATTRIBUTE_IMAGE_MODEL, JSON.stringify(galleryImage));
  }

  private getImageModel(): GalleryImage | undefined
  {
    if (this.get(StageConstants.ATTRIBUTE_IMAGE_MODEL) === undefined) {
      return undefined;
    }
    return this.parseImageModel(this.get(StageConstants.ATTRIBUTE_IMAGE_MODEL));
  }

  private parseImageModel(modelString: string): GalleryImage
  {
    let model = JSON.parse(modelString);
    if (model.expirationDate) {
      model.expirationDate = new Date(model.expirationDate);
    }
    return model;
  }

  public getHref(): string
  {
    return this.get(StageConstants.ATTRIBUTE_HREF)
        || this.element.node.getAttributeNS(
            'http://www.w3.org/1999/xlink', 'href');
  }

  public getBoundingBox(): BoundingBox
  {
    return JSON.parse(
        this.get(StageConstants.ATTRIBUTE_BOUNDING_BOX)
    ) as BoundingBox;
  }

  public approximateEditableArea(): BoundingBox
  {
    const doc = StageUtils.getPartDocument(this.wrapper);
    const constraints = (doc as any).viewbox();

    const wrapperViewBox = this.wrapper.viewbox();
    const boundsRect = (this.wrapper
                        .rect()
                        .move(wrapperViewBox.x, wrapperViewBox.y)
                        .size(wrapperViewBox.width, wrapperViewBox.height));
    const bounds = (boundsRect as any).rbox(doc);
    boundsRect.remove();

    const cx = constraints.x;
    const cy = constraints.y;
    const cw = constraints.width;
    const ch = constraints.height;

    const bx = bounds.x;
    const by = bounds.y;
    const bw = bounds.width;
    const bh = bounds.height;

    const margin = {
      left: cx - bx,
      top: cy - by,
      right: (bx + bw) - (cx + cw),
      bottom: (by + bh) - (cy + ch),
    };

    const normalizedMargin = {
      left: Math.max(margin.left, 0) / bounds.width,
      top: Math.max(margin.top, 0) / bounds.height,
      right: Math.max(margin.right, 0) / bounds.width,
      bottom: Math.max(margin.bottom, 0) / bounds.height,
    };

    return {
      x: normalizedMargin.left * 100,
      y: normalizedMargin.top * 100,
      width: 100 - (normalizedMargin.right + normalizedMargin.left) * 100,
      height: 100 - (normalizedMargin.top + normalizedMargin.bottom) * 100,
    };
  }

  private updateQualityRating(): void
  {
    if (!this.isUnchanged()) {
      this.imageQualityRatingService.readQualityRating(this.element);
    }
  }

  public showPlaceholderHighlighter(): void
  {
    if (!this.placeholderHighlighter) {
      const box = this.wrapper.viewbox();

      this.placeholderHighlighter = this.wrapper.image()
        .size(box.width / 2, box.height / 2)
        .move(box.x + box.width / 4, box.y + box.height / 4)
        .addClass(StageConstants.CLASS_NAME_PLACEHOLDER_HIGHLIGHTER)
        .load(this.PLACEHOLDER_HIGHLIGHTER_IMAGE_PATH);
    }
  }

  public hidePlaceholderHighlighter(): void
  {
    if (this.placeholderHighlighter) {
      this.placeholderHighlighter.remove();
      this.placeholderHighlighter = undefined;
      this.finished.emit();
    }
  }

  protected shouldInjectActionBar(): boolean
  {
    return this.hasEditableContent() && !this.placeholderHighlighter;
  }
}



// WEBPACK FOOTER //
// ./src/app/collage-designer/stage/placeholder/image-placeholder.ts